/**
 * KeyboardShortcuts — Overlay panel showing keyboard shortcuts
 * Press '?' to open. Lists only shortcuts that are wired up (see lib/shortcuts.ts).
 */
import { useEffect, useRef } from 'react';
import { X, Keyboard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { SHORTCUTS, LOCAL_SHORTCUTS, displayKey } from '../../lib/shortcuts';

interface KeyboardShortcutsProps {
  open: boolean;
  onClose: () => void;
}

interface ShortcutGroup {
  title: string;
  shortcuts: { keys: string[]; description: string }[];
}

const GROUP_ORDER = ['navigation', 'chat', 'general'] as const;

function buildGroups(t: TFunction): ShortcutGroup[] {
  return GROUP_ORDER.map(g => ({
    title: t(`shortcuts.group.${g}`),
    shortcuts: [...SHORTCUTS, ...LOCAL_SHORTCUTS]
      .filter(s => s.group === g)
      .map(s => ({ keys: s.keys.map(displayKey), description: t(`shortcuts.${s.label}`) })),
  }));
}

export function KeyboardShortcuts({ open, onClose }: KeyboardShortcutsProps) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
      <div
        ref={panelRef}
        className="bg-nord-0 border border-nord-3/40 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto mx-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kbd-shortcuts-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-nord-3/20 sticky top-0 bg-nord-0 z-10">
          <div className="flex items-center gap-3">
            <Keyboard className="h-5 w-5 text-frost-2" aria-hidden="true" />
            <h2 id="kbd-shortcuts-title" className="text-lg font-semibold text-nord-6">{t('shortcuts.title')}</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-nord-2 transition-colors text-nord-4"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Shortcut groups */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {buildGroups(t).map(group => (
            <div key={group.title}>
              <h3 className="text-sm font-semibold text-frost-2 mb-3 uppercase tracking-wider">
                {group.title}
              </h3>
              <div className="space-y-2">
                {group.shortcuts.map(shortcut => (
                  <div
                    key={shortcut.description}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="text-sm text-nord-4">{shortcut.description}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {shortcut.keys.map((key, i) => (
                        <span key={i}>
                          {i > 0 && <span className="text-nord-muted mx-0.5 text-xs">+</span>}
                          <kbd className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded bg-nord-2 border border-nord-3/40 text-xs font-mono text-nord-5">
                            {key}
                          </kbd>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
